import { useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import UnbeatableSection from '../../Components/UnbeatableSection'
import OurServiceSection from '../../Components/OurServiceSection'
import './styles.scss'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { solid } from '@fortawesome/fontawesome-svg-core/import.macro'
import Input from '../../Components/Input'
import InputArea from '../../Components/InputArea'
import CustomButton from '../../Components/CustomButton'
import SlideShow from '../../Components/SlideShow'

import slide1 from '../../Assets/Images/slide_1.jpeg'
import slide2 from '../../Assets/Images/slide_2.jpeg'
import slide3 from '../../Assets/Images/slide_3.jpeg'
import AccordionGroup from '../../Components/AccordionGroup'
import { SERVICES } from '../../constants'
import { useForm } from '../../Hooks/useForm'
import emailjs from '@emailjs/browser'

const templateId = 'template_tqty2mv'
const serviceId = 'service_pfpb65h'
const publicId = 's6HNn9FJmskIhvYFR'

const icons = [
  <FontAwesomeIcon className="icon" icon={solid('circle-info')} />,
  <FontAwesomeIcon className="icon" icon={solid('truck')} />,
  <FontAwesomeIcon className="icon" icon={solid('plane-up')} />,
]

const AIR_FREIGHT = [
  {
    title: 'Air Charter',
    content:
      'We take on those challenges others might consider impossible. We take maximum care of all requests which are outside the standard airfreight parameters, ensuring your cargo reaches its destination no matter the complexity.',
  },
  {
    title: 'On Board Courier',
    content:
      'With maximum care, dedication and speed — even when all traditional solutions are unavailable — our team will find the perfect solution for urgent shipments that cannot wait.',
  },
  {
    title: 'Air Premium',
    content:
      'Air Premium is the right service for shipments with a tight schedule and a need for time-definite delivery at the final destination, worldwide.',
  },
]

const OCEAN_FREIGHT = [
  {
    title: 'Tailored Solutions',
    content:
      "Meeting customers' unique needs with premium service and local expertise. Flexible options, exception management, frequent sailings, and on-time delivery for every shipment.",
  },
  {
    title: 'Large Global Footprint',
    content:
      'Sterling Logistics offices throughout the world — including high-growth emerging markets — guarantee we can support our customers wherever they operate.',
  },
  {
    title: 'Cargo Visibility and Management',
    content:
      'Market-leading Track & Trace capabilities, online logistics management, powerful reports and analytics, and CO2 reporting for complete supply chain transparency.',
  },
]

const CARGO_TRANSPORTATION = [
  {
    title: 'Express Cargo',
    content:
      'Time-sensitive shipments handled with priority routing and dedicated transport, ensuring your cargo reaches its destination on schedule with zero compromise on care.',
  },
  {
    title: 'Oversized & Heavy Cargo',
    content:
      'Specialized handling for oversized, heavy, or irregularly shaped cargo requiring custom transport solutions — including flatbeds, low-loaders, and heavy-lift equipment.',
  },
  {
    title: 'Cross-Border Transport',
    content:
      'Seamless cross-border cargo transportation with full customs documentation support, ensuring compliance and fast clearance at every border crossing.',
  },
]

const PACKAGING_AND_STORAGE = [
  {
    title: 'Food Grade Warehouse',
    content:
      'We understand the challenges of storing, shipping, and handling inventory in a food-grade environment. Our certified facilities maintain the strictest hygiene and temperature standards.',
  },
  {
    title: 'Distribution Center',
    content:
      'Effective distribution requires real-time strategy. We focus on exceeding your distribution center needs while using industry insights and technology to predict them before they arise.',
  },
  {
    title: 'Dedicated Warehouse',
    content:
      'When you need dedicated warehousing space with a committed team, Sterling Logistics delivers flexible short-term and long-term agreements tailored to your business.',
  },
]

const Service = () => {
  const { state } = useLocation()

  const [activeService, setActiveService] = useState(SERVICES[0])

  useEffect(() => {
    if (state?.service) {
      setActiveService(state.service)
    }
  }, [state])

  const handleServiceClick = (index) => () => {
    setActiveService(SERVICES[index])
  }

  const getServiceTitle = () => {
    switch (activeService) {
      case SERVICES[0]:
        return 'Our global network has the power to help businesses grow — built on 34 years of experience and shaped by the evolving needs of our customers.'

      case SERVICES[1]:
        return 'Our extensive experience in sea freight transport allows us to offer quality, reliable services for your imports and exports, handled with care from port to destination.'

      case SERVICES[2]:
        return 'Sterling Logistics Cargo handles freight around the clock, performing express customs clearance of exported and imported goods in accordance with IATA standards — keeping your supply chain moving.'

      case SERVICES[3]:
        return 'Sterling Logistics offers ambient, air-conditioned, and open yard storage solutions tailored to meet every warehousing need, no matter the cargo type or volume.'

      default:
        return ''
    }
  }

  const getServiceContent = () => {
    switch (activeService) {
      case SERVICES[0]:
        return `Airfreight is fast-moving, challenging and constantly changing — it's the nature of the business. Around the globe, capacity and demand can shift in the blink of an eye, so it's essential to have a partner who provides stability and reliability to maintain your supply chain's total integrity. Whether your shipments fly in the bellies of commercial planes or require specialist freighter aircraft, Sterling Logistics provides tailor-made solutions for all your airfreight needs.\n\nSterling Logistics is among the most trusted air freight providers in our markets, offering a comprehensive package of premium, value-added services and specialist products — each specifically tailored to your operation. Across our network you can choose the service that best suits your needs, from express courier to bulk charter.\n\nRegardless of the size of your business, our dedicated implementation teams ensure a smooth onboarding, while our tracking platform keeps you up to date on the status of every shipment in real time.\n\nCall or email us today to speak with one of our specialists and discover new opportunities for your air freight supply chain.`

      case SERVICES[1]:
        return `Whether container load or special transport: as a reliable partner for your sea freight requirements, Sterling Logistics manages all orders competently, carefully and efficiently via desired ports worldwide. We charter fixed cargo space on key sea freight routes to safeguard hold capacity for our clients.\n\nAll transports are fully monitored with state-of-the-art IT systems. Combined with unified quality standards, this guarantees our customers a high level of transparency and planning security throughout every stage of the journey.\n\nWe provide comprehensive guidance on all relevant conditions — including customs regulations, transport insurance requirements, and import/export documentation — ensuring the complete handling process is managed expertly from start to finish.\n\nWe are happy to answer any questions regarding available destinations, freight options, estimated transit times, or pricing. Contact our team for a tailored ocean freight solution built around your business.`

      case SERVICES[2]:
        return `Sterling Logistics Cargo handles the full spectrum of surface and air cargo, with operations running around the clock to ensure your shipments are processed efficiently and delivered on time. Our terminals are equipped for express customs clearance of imported and exported goods in line with international IATA standards.\n\nOur facilities include climate-controlled chambers for temperature-sensitive goods and designated storage areas for oversized cargo, supported by heavy-lift equipment capable of managing large tonnage loads.\n\nSterling Logistics partners with major carriers across multiple routes, giving clients the flexibility to select the most suitable carrier, routing, and pricing for their specific shipment requirements.\n\nOur operations team is available to provide full route and destination information and is ready to establish long-term service agreements for businesses with regular transportation needs. Competitive rates are maintained through direct carrier relationships and the elimination of unnecessary intermediaries.`

      case SERVICES[3]:
        return `Sterling Logistics offers comprehensive ambient, air-conditioned, and open yard storage solutions designed to meet every warehousing requirement. Our strategically located facilities provide easy access to major logistics networks and commercial centers, ensuring efficient distribution and inventory management for your business.\n\nOur warehousing services are technology-driven — from warehouse management systems that provide real-time inventory visibility, to streamlined picking and packing processes that reduce handling time and minimize errors across your supply chain.\n\nFacilities include dedicated food-grade warehousing, distribution centers, and secure open yard storage for oversized or heavy items. Every location is climate-controlled and protected by 24-hour security, ensuring your cargo remains safe and accessible at all times.\n\nWe tailor our storage solutions to each client's unique requirements, offering flexible short-term and long-term agreements. Contact our team to design a warehousing solution that fits your operation.`

      default:
        return ''
    }
  }

  const getAccordionData = () => {
    const base = icons.map((icon) => ({ icon }))

    switch (activeService) {
      case SERVICES[0]:
        return base.map((acc, idx) => ({
          ...acc,
          title: AIR_FREIGHT[idx].title,
          content: AIR_FREIGHT[idx].content,
        }))

      case SERVICES[1]:
        return base.map((acc, idx) => ({
          ...acc,
          title: OCEAN_FREIGHT[idx].title,
          content: OCEAN_FREIGHT[idx].content,
        }))

      case SERVICES[2]:
        return base.map((acc, idx) => ({
          ...acc,
          title: CARGO_TRANSPORTATION[idx].title,
          content: CARGO_TRANSPORTATION[idx].content,
        }))

      case SERVICES[3]:
        return base.map((acc, idx) => ({
          ...acc,
          title: PACKAGING_AND_STORAGE[idx].title,
          content: PACKAGING_AND_STORAGE[idx].content,
        }))

      default:
        return []
    }
  }

  const { form, reset, onChange } = useForm({
    name: '',
    email: '',
    phone: '',
    origin: '',
    destination: '',
    service_type: activeService,
    estimated_weight: '',
    msg: '',
    loading: false,
  })

  const handleChange = (e) => onChange(e.target.name, e.target.value)

  const handleSubmit = async (e) => {
    e.preventDefault()

    onChange('loading', true)

    try {
      await emailjs.send(serviceId, templateId, form, publicId)

      reset()
    } catch (error) {
      console.log('ERROR', error)
    }

    onChange('loading', false)
  }

  return (
    <div className="service">
      <div className="service__top">
        <h1>{activeService}</h1>
      </div>

      <div className="service__content">
        <section className="service-content">
          <div className="main-content">
            <SlideShow slideImages={[slide1, slide2, slide3]} />

            <h2 className="main-content__title">{getServiceTitle()}</h2>

            <div className="main-content__content">{getServiceContent()}</div>

            <h4>Our services: choose the right fit for your specific needs</h4>

            <div className="accordion-container">
              <AccordionGroup data={getAccordionData()} />
            </div>
          </div>

          <div className="side-menu">
            <div className="services-list">
              <ul>
                <li
                  className={activeService === SERVICES[0] ? 'active' : undefined}
                  onClick={handleServiceClick(0)}
                >
                  <div>Air Freight</div>
                </li>

                <li
                  className={activeService === SERVICES[1] ? 'active' : undefined}
                  onClick={handleServiceClick(1)}
                >
                  <div>Ocean Freight</div>
                </li>

                <li
                  className={activeService === SERVICES[2] ? 'active' : undefined}
                  onClick={handleServiceClick(2)}
                >
                  <div>Cargo Transportation</div>
                </li>

                <li
                  className={activeService === SERVICES[3] ? 'active' : undefined}
                  onClick={handleServiceClick(3)}
                >
                  <div>Packaging and Storage</div>
                </li>
              </ul>
            </div>

            <div className="contact-widget">
              <div className="location">
                <h3>LOCATION</h3>

                <div className="location__content">
                  <FontAwesomeIcon className="icon" icon={solid('location-dot')} />
                  <div>
                    <span>Location:</span>
                    <span>GLOBAL FACILITY OFFICE</span>
                  </div>
                </div>
              </div>

              <div className="call-center">
                <h3>CALL CENTER</h3>

                <div className="call-center__content">
                  <FontAwesomeIcon className="icon" icon={solid('phone')} />
                  <div>
                    <span>Call Us:</span>
                    <span>+1 (512) 643-7634</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="contact-form">
              <h3>Get A Free Quote</h3>
              <p className="contact-form__subtitle">Tell us about your shipment and we'll respond within 24 hours.</p>

              <form onSubmit={handleSubmit}>
                <Input
                  title="Full Name"
                  required
                  placeholder="John Doe"
                  value={form.name}
                  onChange={handleChange}
                  name="name"
                />

                <Input
                  title="Email Address"
                  type="email"
                  required
                  placeholder="john@example.com"
                  value={form.email}
                  onChange={handleChange}
                  name="email"
                />

                <Input
                  title="Phone Number"
                  type="tel"
                  placeholder="+1 (555) 000-0000"
                  value={form.phone}
                  onChange={handleChange}
                  name="phone"
                />

                <Input
                  title="Origin / Pickup Location"
                  required
                  placeholder="City, Country"
                  value={form.origin}
                  onChange={handleChange}
                  name="origin"
                />

                <Input
                  title="Destination"
                  required
                  placeholder="City, Country"
                  value={form.destination}
                  onChange={handleChange}
                  name="destination"
                />

                <Input
                  title="Estimated Weight / Dimensions"
                  placeholder="e.g. 500 kg / 2×1×1 m"
                  value={form.estimated_weight}
                  onChange={handleChange}
                  name="estimated_weight"
                />

                <InputArea
                  title="Additional Notes"
                  rows={3}
                  placeholder="Special handling, hazardous materials, preferred dates..."
                  value={form.msg}
                  onChange={handleChange}
                  name="msg"
                />

                <CustomButton
                  otherClassName="submit"
                  loading={form.loading}
                  type="submit"
                  label="REQUEST QUOTE"
                />
              </form>
            </div>
          </div>
        </section>

        <UnbeatableSection />

        <OurServiceSection />
      </div>
    </div>
  )
}

export default Service
